import React from "react";
import styled, { css } from "styled-components";
import { DebounceInput } from "react-debounce-input";
import useKeyboardShortcut from "use-keyboard-shortcut";
import * as _ from "lodash";

import { InputCss } from "src/library/components/Forms/Input";
import { ISearchRequest } from "src/core/products/api/products.api";
import { useSearchApi } from "src/core/shared/api/search.api";
import { Text } from "src/library/components/Text";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { Tooltip } from "src/library/components/Tooltip";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";

import { ProductQuantityRef } from "../ProductQuantity";
import useProductSearchFilters from "../../hooks/useProductSearchFilters";
import { RapidOrderEntryCard } from "./RapidOrderEntryCard";

export const RapidOrderEntry: React.ReactFCWithChildren = () => {
  const productSearchFilters = useProductSearchFilters();
  const [ listSearchResponse, getSearch ] = useSearchApi("SEARCH_PRODUCTS");
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [ selectedCardIndex, setSelectedCardIndex ] = React.useState<number | null>();
  const [ focusedCardIndex, setFocusedCardIndex ] = React.useState<number | null>();
  const cardRef = React.useRef<ProductQuantityRef[]>([]);

  /**
   * Finds available products to add to the change list
   *
   * @param {*} props
   * @returns
   */
  const performSearch = React.useCallback(
    (searchTerm: ISearchRequest) => {
      if (searchTerm.length) {
        const filters = productSearchFilters(false);

        setFocusedCardIndex(null);
        setSelectedCardIndex(null);

        getSearch({
          pagination: {
            offset: 0,
            limit: 100,
            page: 0,
            sort: [ [ "description", "DESC" ] ]
          },
          filters: {
            ...filters,
            $or: [ { description: { $like: `%${searchTerm}%` } }, { partNumber: { $like: `%${searchTerm}%` } } ]
          }
        });
      }
    },
    [ getSearch, productSearchFilters ]
  );

  const onBlur = React.useCallback(() => {
    if (!selectedCardIndex) {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
    }

    setFocusedCardIndex(null);
  }, [ selectedCardIndex ]);

  const onFocus = React.useCallback((index: number) => {
    setFocusedCardIndex(index);
    setSelectedCardIndex(index);
  }, []);

  /**
   * Use control + s as a short cut to search
   */
  useKeyboardShortcut(
    [ "Control", "S" ],
    () => {
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
    },
    {
      overrideSystem: false,
      ignoreInputFields: false,
      repeatOnHold: false
    }
  );

  /**
   * Make pressing enter work a bit more intuitively
   */
  useKeyboardShortcut(
    [ "Enter" ],
    () => {
      if (typeof selectedCardIndex === "number") {
        if (typeof focusedCardIndex === "number") {
          cardRef.current[ focusedCardIndex ].inputRef?.blur();
        } else {
          cardRef.current[ selectedCardIndex ].focus();
        }
      }
    },
    {
      overrideSystem: true,
      ignoreInputFields: false,
      repeatOnHold: false
    }
  );

  /**
   * use a tab hotkey to tab into a card
   */
  useKeyboardShortcut(
    [ "Tab" ],
    () => {
      const cardsLength = listSearchResponse.data?.result.length || 0;

      if (cardsLength) {
        let newIndex = (typeof selectedCardIndex === "number" ? selectedCardIndex + 1 : 0);

        if (newIndex > (cardsLength - 1)) {
          newIndex = 0;
        }

        setSelectedCardIndex(newIndex);

        if (cardRef.current[ newIndex - 1 ]) {
          cardRef.current[ newIndex - 1 ].inputRef?.blur();
        } else if (newIndex === 0 && cardRef.current[ cardsLength - 1 ]) {
          cardRef.current[ cardsLength - 1 ].inputRef?.blur();
        }

        if (cardRef.current[ newIndex ].quantity) {
          cardRef.current[ newIndex ].focus();
        } else {
          setFocusedCardIndex(null);
        }
      }
    },
    {
      overrideSystem: true,
      ignoreInputFields: false,
      repeatOnHold: false
    }
  );

  return (
    <StyledRapidOrderWrapper>
      <div className="rapid-header">
        <Text as="h4">
          Quickly add products to this order
        </Text>
      </div>

      <div className="rapid-body">
        <div className="rapid-search">
          <DebounceInput
            placeholder="Search products by name or code"
            className="rapid-input"
            inputRef={inputRef}
            debounceTimeout={500}
            onChange={e => performSearch(e.target.value)}
          />

          <div
            data-tip
            data-for="keyboard-shortcuts"
            className="keyboard-shortcuts"
          >
            <Icon icon={Icons.KEYBOARD} />
          </div>

          <Tooltip
            id="keyboard-shortcuts"
            type="dark"
            place="top"
            getContent={() => {
              return (
                <Text
                  as="p"
                  color="white"
                >
                  <Text color="white">
                    Control (
                    <Icon
                      icon={Icons.KEYBOARD_CONTROL}
                      size={10}
                      $fillColor="white"
                    />

                    {") + S "}
                    - search for a product
                  </Text>

                  <br />

                  <Text color="white">
                    Tab (
                    <Icon
                      icon={Icons.KEYBOARD_TAB}
                      size={10}
                      $fillColor="white"
                    />
                    ) - select next product
                  </Text>

                  <br />

                  <Text color="white">
                    Enter (
                    <Icon
                      icon={Icons.KEYBOARD_RETURN}
                      size={10}
                      $fillColor="white"
                    />
                    ) - add a product or confirm amount
                  </Text>
                </Text>
              );
            }}
          />

        </div>

        <div className="product-cards">
          {listSearchResponse.pending ? (
            <div className="loading-card">
              <LoadingSpinner
                type="circular-pulse"
                position="inline"
                size={35}
              />
            </div>
          ) :
            !!inputRef.current?.value.length && !!listSearchResponse.data?.result.length && _.map(listSearchResponse.data.result, (product, index) => (
              <>
                <RapidOrderEntryCard
                // attach the ref into an array so we can access each index as a ref
                  productQuantityRef={prodQuantityRef => {
                    if (prodQuantityRef) {
                      cardRef.current[ index ] = prodQuantityRef;
                    }
                  }}
                  key={`rapid-order-entry-card-${product.id}`}
                  product={product}
                  onBlur={onBlur}
                  onFocus={() => onFocus(index)}
                  className={`${selectedCardIndex === index ? "active" : ""}`}
                />
              </>
            ))}

          {listSearchResponse.data && !listSearchResponse.data?.result.length && (
            <Text>
              No products matched your search
            </Text>
          )}
        </div>
      </div>
    </StyledRapidOrderWrapper>
  );
};

const StyledRapidOrderWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    margin-bottom: ${theme.spacing.margins.xl};
    background: ${theme.colors.white};
    
    .rapid-header {
      background: ${theme.colors.lightGrey};
      padding: ${theme.spacing.padding.md};

      h4 {
        margin-bottom: 0;
      }
    }

    .rapid-body {
      padding: ${theme.spacing.padding.md};

      .rapid-search {
        display: flex;
        align-items: center;

        .keyboard-shortcuts {
          margin-left: ${theme.spacing.margins.sm};

          @media (hover: none) and (pointer: coarse) {
            display: none;
          }
        }
      }

      .product-cards {
        margin-top: ${theme.spacing.margins.md};
        display: flex;
        flex-direction: row;
        overflow-x: scroll;
      }

      .rapid-input {
        ${InputCss}
      }

      .loading-card {
        width: 195px;
        height: 145px;
        background: ${theme.colors.white};
        padding: ${theme.spacing.padding.md};
        border: 1px solid ${theme.colors.lightGrey};
        border-radius: ${theme.borderRadius};
        margin-right: ${theme.spacing.margins.md};
        display: flex;
        flex-direction: column;
        justify-content: center;
      }
    }
  `}
`;
